import { useId, useState } from 'react';
import { useOrgId } from '@/app/session';
import { Avatar, Button, Icon } from '@/components/ui';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import { addressesOf, splitMessage, type Message } from './api';
import { HoverTime } from './parts';
import { RecordedMessage } from './RecordedThread';
import './conversations.css';

const BLOB_NOTE: Partial<Record<Message['attachments'][number]['blob_status'], string>> = {
  uploading: 'arriving',
  quarantined: 'being checked',
  rejected: 'blocked',
  unavailable: 'unavailable',
};

/**
 * One letter in the correspondence: the author's avatar, their name ("You" for us), the new text at a
 * reading measure, quoted history and signature folded, attachments as chips. Our side sits on a
 * slightly different surface tone. Provenance lives in Message details, never here.
 */
export function MessageArticle({
  message,
  sender,
  senderRole,
  company,
  ours = false,
  headingLevel = 3,
}: {
  message: Message;
  /** Display label for the author ("You", a colleague); defaults to the From name or address. */
  sender?: string;
  /** Faint "role, company" after the name. */
  senderRole?: string | null;
  /** Company of the author, for the avatar tint. */
  company?: string | null;
  ours?: boolean;
  headingLevel?: 2 | 3;
}) {
  const orgId = useOrgId();
  const [showHidden, setShowHidden] = useState(false);
  const [showRetracted, setShowRetracted] = useState(false);
  const hiddenId = useId();
  if (message.kind === 'source_recorded') return <RecordedMessage message={message} />;
  const H = headingLevel === 2 ? 'h2' : 'h3';
  const from = addressesOf(message, 'from')[0];
  const fromName = from?.display_name ?? from?.email_original;
  // Always the person; where the record came from is in Message details.
  const label = sender ?? fromName ?? (message.kind === 'manual' ? 'Note' : 'Unknown sender');
  const parts = message.text_body ? splitMessage(message.text_body, from?.display_name) : null;
  const notes = message.annotations.filter((a) => a.annotation_kind === 'note' || a.annotation_kind === 'correction');

  return (
    <article
      id={`message-${message.id}`}
      className={cx('msg', ours ? 'msg-ours' : 'msg-theirs')}
      aria-label={`${label}, ${formatDateTime(message.occurred_at)}`}
    >
      <Avatar name={fromName ?? label} company={company} size={36} className="msg-avatar" />
      <div className="msg-main">
        <header className="msg-head">
          <H className="msg-sender">{label}</H>
          {senderRole ? <span className="msg-role">{senderRole}</span> : null}
          <span className="msg-time">
            <HoverTime value={message.occurred_at} />
          </span>
        </header>

        {message.retracted && !showRetracted ? (
          <p className="msg-quiet">
            Withdrawn{' '}
            <Button size="sm" variant="quiet" onClick={() => setShowRetracted(true)}>
              Show message
            </Button>
          </p>
        ) : parts ? (
          <div className="msg-body">
            <div className="msg-text">
              {parts.body.split(/\n\s*\n/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
            {parts.hidden ? (
              <>
                <button
                  type="button"
                  className="msg-toggle"
                  aria-expanded={showHidden}
                  aria-controls={hiddenId}
                  aria-label={showHidden ? 'Hide quoted text and signature' : 'Show quoted text and signature'}
                  title={showHidden ? 'Hide quoted text and signature' : 'Show quoted text and signature'}
                  onClick={() => setShowHidden((v) => !v)}
                >
                  <Icon name="more" size={16} />
                </button>
                {showHidden ? (
                  <p id={hiddenId} className="msg-text msg-quoted">
                    {parts.hidden}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        ) : (
          <p className="msg-quiet">{message.has_sanitized_html ? 'Formatted content only' : 'No text'}</p>
        )}

        {message.attachments.length > 0 ? (
          <ul className="msg-files" aria-label="Attachments">
            {message.attachments.map((a) => {
              const name = a.filename ?? 'Unnamed file';
              const note = BLOB_NOTE[a.blob_status];
              return (
                <li key={`${a.blob_id}-${a.part_index}`}>
                  {a.blob_status === 'clean' ? (
                    <a className="file-chip" href={`/api/v1/orgs/${orgId}/media/${a.blob_id}/content`} download={name}>
                      <Icon name="file" size={14} />
                      {name}
                    </a>
                  ) : (
                    <span className="file-chip file-chip-off">
                      <Icon name="file" size={14} />
                      {name}
                      {note ? <span className="faint">, {note}</span> : null}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}

        {notes.length > 0 ? (
          <ul className="msg-notes">
            {notes.map((n) => (
              <li key={n.id}>
                <span className="strong">{n.annotation_kind === 'correction' ? 'Correction' : 'Note'}:</span> {n.text}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </article>
  );
}
